import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { submitInquiry } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import PhoneInput from "../components/PhoneInput";
import SEO from "../components/SEO";
import { useI18n } from "../lib/i18n";
import { useToast } from "../hooks/use-toast";
import { Loader2, CheckCircle2, Sparkles, ArrowLeft } from "lucide-react";

const INTEREST_OPTIONS = [
  { value: "top_sites_museums", label: "Top sites & museums" },
  { value: "food", label: "Food" },
  { value: "off_the_beaten_path", label: "Off the beaten path" },
  { value: "private_vehicle", label: "Comfortable tour with private vehicle" },
];

const DAILY_LENGTH_OPTIONS = [
  { value: "3-4", label: "3–4 hours daily" },
  { value: "6", label: "6 hours daily" },
];

const EMPTY = {
  full_name: "",
  email: "",
  phone_country_code: "+1",
  phone: "",
  days: "",
  daily_length: "",
  adults: "",
  children: "",
  babies: "",
  interests: [],
  special_interest: "",
};

const TailorMadeTourPage = () => {
  const { lang } = useI18n();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleInterest = (value) =>
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(value) ? f.interests.filter((v) => v !== value) : [...f.interests, value],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const interestLabels = form.interests
        .map((v) => INTEREST_OPTIONS.find((o) => o.value === v)?.label)
        .filter(Boolean);

      const details = [
        form.days && `Number of days: ${form.days}`,
        form.daily_length && `Preferred daily length: ${DAILY_LENGTH_OPTIONS.find((o) => o.value === form.daily_length)?.label || form.daily_length}`,
        `Adults (12+): ${form.adults || 0}`,
        `Children (6–11): ${form.children || 0}`,
        `Babies: ${form.babies || 0}`,
        interestLabels.length > 0 && `Interested in: ${interestLabels.join(", ")}`,
        form.special_interest && `Special interest: ${form.special_interest}`,
      ]
        .filter(Boolean)
        .join("\n");

      await submitInquiry({
        full_name: form.full_name,
        email: form.email,
        phone: form.phone || null,
        phone_country_code: form.phone ? form.phone_country_code : null,
        subject: "Tailor-made Istanbul Tour Request",
        message: details,
        language: lang,
      });
      setDone(true);
      toast({ title: "Request received", description: "We'll be in touch to design your Istanbul tour." });
      setForm(EMPTY);
      setTimeout(() => {
        navigate("/thank-you", {
          state: {
            heading: "Your tour request is in.",
            message: "We'll design a private Istanbul itinerary around what you told us and follow up within a day.",
          },
        });
      }, 1200);
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: err?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-16">
      <SEO
        title="Tailor-Made Istanbul Tour · Istanbul Free Walking Tours"
        description="Tell us about your group and interests, and we'll design a private, custom Istanbul tour built entirely around you."
        path="/tailor-made-tour"
      />
      <section className="section-tight bg-[var(--ri-cream)]">
        <div className="container-x max-w-[640px]">
          <Link to="/" className="inline-flex items-center gap-1.5 text-[13.5px] text-[var(--ri-muted)] hover:text-[var(--ri-red)] mb-6">
            <ArrowLeft size={14} /> Back to home
          </Link>
          <p className="eyebrow mb-3 inline-flex items-center gap-2">
            <Sparkles size={14} /> Tailor-Made Tour
          </p>
          <h1 className="font-serif-display text-[32px] md:text-[40px] leading-[1.08]">
            Design your own Istanbul tour.
          </h1>
          <p className="mt-4 text-[15.5px] leading-[1.75] text-[var(--ri-ink-soft)]">
            Tell us about your group, how you like to travel, and what you're most interested in — we'll put together a private itinerary built entirely around you.
          </p>

          {done ? (
            <div className="mt-10 py-10 text-center rounded-2xl border border-[var(--ri-border)] bg-[#fffdf7]">
              <div className="mx-auto w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-3">
                <CheckCircle2 className="text-green-600" size={28} />
              </div>
              <p className="text-[16px] font-medium">Thanks — your request is in.</p>
              <p className="mt-1 text-[14px] text-[var(--ri-muted)]">We'll reply within a day to start planning your tour.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-10 space-y-4">
              <div>
                <Label className="text-[13px]">Full name</Label>
                <Input required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label className="text-[13px]">Email</Label>
                <Input type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-1" />
              </div>
              <PhoneInput
                label="Phone / WhatsApp number"
                code={form.phone_country_code}
                number={form.phone}
                onCodeChange={(v) => update("phone_country_code", v)}
                onNumberChange={(v) => update("phone", v)}
              />

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[13px]">Number of days</Label>
                  <Input type="number" min="1" value={form.days} onChange={(e) => update("days", e.target.value)} className="mt-1" placeholder="e.g. 3" />
                </div>
                <div>
                  <Label className="text-[13px] mb-1 block">Preferred daily length</Label>
                  <div className="flex gap-2">
                    {DAILY_LENGTH_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => update("daily_length", opt.value)}
                        className={`flex-1 text-[13px] font-medium px-3 py-2.5 rounded-md border transition-colors ${form.daily_length === opt.value ? "bg-[var(--ri-red)] text-white border-[var(--ri-red)]" : "bg-white text-[var(--ri-ink-soft)] border-[var(--ri-border)]"}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-[13px]">Adults (12+)</Label>
                  <Input type="number" min="0" value={form.adults} onChange={(e) => update("adults", e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label className="text-[13px]">Children (6–11)</Label>
                  <Input type="number" min="0" value={form.children} onChange={(e) => update("children", e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label className="text-[13px]">Babies</Label>
                  <Input type="number" min="0" value={form.babies} onChange={(e) => update("babies", e.target.value)} className="mt-1" />
                </div>
              </div>

              <div>
                <Label className="text-[13px] mb-2 block">What are you most interested in? (choose any)</Label>
                <div className="flex flex-wrap gap-2">
                  {INTEREST_OPTIONS.map((opt) => {
                    const active = form.interests.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => toggleInterest(opt.value)}
                        className={`text-[13px] font-medium px-3.5 py-2 rounded-full border transition-colors ${active ? "bg-[var(--ri-red)] text-white border-[var(--ri-red)]" : "bg-white text-[var(--ri-ink-soft)] border-[var(--ri-border)]"}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label className="text-[13px]">Any special interest? (optional)</Label>
                <Textarea rows={4} value={form.special_interest} onChange={(e) => update("special_interest", e.target.value)} className="mt-1" placeholder="Tell us anything else that would help us plan your tour." />
              </div>

              <button type="submit" disabled={submitting} className="btn-primary w-full sm:w-auto inline-flex items-center justify-center gap-2">
                {submitting && <Loader2 size={16} className="animate-spin" />}
                Send request
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default TailorMadeTourPage;
